import type {
  AddressLookupRequest,
  AddressLookupResponse,
  AddressSuggestResponse,
  ServiceableZipsResponse,
  LeadCaptureRequest,
  LeadCaptureResponse,
  SessionResponse,
} from '@rheora/enrollment-contract';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';


import type { DeepLinkSource } from '../schemas/shop.schema';
import { establishingFunnelSession } from '../services/funnel-session.service';
import { recallSource } from '../services/source-attribution.service';

import { funnelQueryKeys } from './funnel.queries';
import {
  ensureSession,
  getAddressSuggestions,
  getServiceableZips,
  postAddressLookup,
  postLead,
} from './serviceability.fetchers';

/**
 * TanStack hooks for E-SF03. Pages call THESE, never the fetchers. The lookup and
 * lead are mutations (they are POSTs with side effects + idempotency keys); the
 * session ensure is a one-shot query fired on mount.
 */

export const serviceabilityQueryKeys = {
  /**
   * ⚠️ KEYED BY THE ATTRIBUTION SOURCE. `staleTime: Infinity` means this query is
   * fetched once and then never again — so with a bare key, a shopper who
   * established a session organically and THEN followed a Power to Choose deep
   * link would never make the call that reports `ptc`. Including the source makes
   * the flip organic → ptc a different key, i.e. one refetch, once, exactly when
   * the attribution changes.
   */
  session: (source: DeepLinkSource = 'organic') => ['shop', 'session', source] as const,
  addressSuggest: (q: string) => ['shop', 'address-suggest', q] as const,
  serviceableZips: () => ['shop', 'serviceable-zips'] as const,
} as const;

/**
 * Address autocomplete (demo mock). The caller passes an ALREADY-DEBOUNCED query;
 * this hook only fires when it is non-trivial (>= 2 chars), so a single keystroke
 * never hits the BFF. Results are cached per query string (a re-type of the same
 * partial reuses them); never retried (a public GET behind the abuse floor).
 */
/**
 * The serviceable city+ZIP list for the ZIP picker.
 *
 * Fetched ONCE and cached for the session (`staleTime: Infinity`): the catalogue
 * is a build-time constant on the BFF, so re-fetching it as the shopper types
 * would be pure waste. Gated on `enabled` because the BFF route requires a funnel
 * session — firing before the session mint would just 401.
 */
export function useServiceableZipsQuery(enabled: boolean): UseQueryResult<ServiceableZipsResponse> {
  return useQuery({
    queryKey: serviceabilityQueryKeys.serviceableZips(),
    queryFn: getServiceableZips,
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useAddressSuggestQuery(
  debouncedQuery: string,
): UseQueryResult<AddressSuggestResponse> {
  const q = debouncedQuery.trim();
  return useQuery({
    queryKey: serviceabilityQueryKeys.addressSuggest(q),
    queryFn: () => getAddressSuggestions(q),
    enabled: q.length >= 2,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Establish the funnel session for THIS page load — a NEW one every time.
 *
 * Product decision 2026-09-16: nothing typed into the funnel survives a refresh.
 * So the one-shot session call asks the BFF for `fresh=1`, which retires the
 * session the cookie named and mints an empty one. Fired first by
 * `FunnelHydrationGate` (so no step can read the old session back) and shared
 * by the landing chooser and the ZIP / ESI ID sub-flows through the query cache
 * — one mint per page load, never one per panel. Never retried on the client's
 * account — a session mint is IP-rate-limited server-side.
 *
 * ⚠️ RELEASES THE TERMINAL-SESSION LATCH on success — a fresh session means the
 * suppressed `GET /bff/funnel` read is worth asking again
 * (`funnel-session.service.ts`).
 */
export function useEnsureSessionQuery(
  options: { readonly fresh?: boolean } = {},
): UseQueryResult<SessionResponse> {
  // The `?src=` this visit landed on, remembered by `useDeepLinkSource` on the
  // deep-link page — which is a DIFFERENT page from this one, and is why the value
  // is recalled from the visit store rather than read off the current URL.
  const source = recallSource();
  // ⚠️ `fresh` DEFAULTS TO TRUE and must stay that way — an ordinary page load
  // starts over (2026-09-16). The ONE caller that passes `false` is the offer
  // arrival, where the BFF minted this session and seeded the meter into it
  // moments ago; retiring it there deleted the whole offer. See
  // `STOREFRONT_OFFER_ARRIVAL_PARAM`.
  const fresh = options.fresh !== false;
  return useQuery({
    // ⚠️ `fresh` IS DELIBERATELY *NOT* PART OF THE KEY. It looks like it should be
    // — the two calls do different things — and putting it there is a bug I
    // shipped and had to take back out:
    //
    //   The gate calls this with `fresh:false` on an offer arrival, caching under
    //   a `keep` key. Every OTHER caller (the landing chooser, the ZIP / ESI ID
    //   sub-flows) takes the default, which under a split key is a DIFFERENT
    //   cache entry — so one of them fired a real `fresh=1` mint part-way through
    //   the funnel and deleted the offer-seeded session. The customer got as far
    //   as the credit step and was refused with "Resolve a service address before
    //   running a credit check", because the candidate had just been thrown away.
    //
    // ONE key means the gate — which blocks on `isPending` before anything else
    // mounts — establishes the session for the whole page load, and later callers
    // observe it through the cache (`staleTime: Infinity`) rather than re-minting.
    // `fresh` is a property of THE LOAD, not of the caller.
    queryKey: serviceabilityQueryKeys.session(source),
    queryFn: () => establishingFunnelSession(() => ensureSession(source, { fresh })),
    staleTime: Infinity,
    retry: false,
    // ⚠️ A FAILED MINT MUST NOT BE RE-ASKED BY EVERY MOUNT. `staleTime` only
    // protects a query that HOLDS DATA; an errored, data-less query counts as
    // stale, so each panel that mounts and observes this key would refetch it.
    // With the gate blocking on `isPending`, that is a loop: the mint fails →
    // the gate renders the funnel → a panel mounts and refetches → the gate
    // shows its spinner again → the panel unmounts → the fetch fails → repeat,
    // one `GET /bff/session` per cycle, forever (the same shape as the 767-
    // request storm in funnel-session.service.ts). A refused mint is answered
    // once per page load; a reload is the recovery.
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * The address → serviceability → ESI resolution mutation.
 *
 * Writes server-side candidate records into the funnel session, so the cached
 * `GET /bff/funnel` snapshot is stale on success — and, like the ESI ID lookup,
 * it establishes a session, so it releases the terminal-session latch.
 */
export function useAddressLookupMutation(): UseMutationResult<
  AddressLookupResponse,
  Error,
  AddressLookupRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AddressLookupRequest) =>
      establishingFunnelSession(() => postAddressLookup(request)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}

/** The out-of-area lead-capture mutation (AC2). */
export function useLeadCaptureMutation(): UseMutationResult<
  LeadCaptureResponse,
  Error,
  LeadCaptureRequest
> {
  return useMutation({ mutationFn: postLead });
}
